import React from "react";
import AlertModal from "../../components/AlertModal";
import useProfileData from "./hooks/useProfileData";
import { getResponsiveStyles, getHoverStyles, getDefaultStyles } from "./utils/profileStyles";
import ProfileHeader from "./components/ProfileHeader";
import DetailsTab from "./components/DetailsTab";
import PerformanceTab from "./components/PerformanceTab";
import ProjectsTab from "./components/ProjectsTab";
import CheckInReminderModal from "./components/CheckInReminderModal";
import { SkeletonStyles, SkeletonBox, LoadingPill } from "../../components/SkeletonLoader";
const ProfilePage = () => {
  const {
    theme, language, isRTL, t,
    screenSize,
    profilePicture, isUploading,
    activeTab,
    alertModal, closeAlert,
    fileInputRef,
    handleFileSelect, triggerFileInput,
    employeeData, performanceData, recentActivities,
    showReminderModal, setShowReminderModal,
    hasTodayCheckIn, hasTodayCheckOut, checkInTime, checkOutTime,
    profileLoading,
  } = useProfileData();

  const styles = getResponsiveStyles(theme, screenSize, isRTL);

  if (profileLoading) {
    return (
      <div style={{ ...styles.container, marginTop: "-30px", marginBottom: "20px" }}>
        <SkeletonStyles theme={theme} />
        <LoadingPill
          text={isRTL ? "جارٍ تحميل الملف الشخصي..." : "Loading profile information..."}
          theme={theme}
          isRTL={isRTL}
        />

        {/* Profile header skeleton */}
        <div style={styles.profileHeader}>
          <div style={{ ...styles.profileHeaderContent }}>
            <SkeletonBox width={screenSize.isMobile ? "100px" : "140px"} height={screenSize.isMobile ? "100px" : "140px"} style={{ borderRadius: "50%", flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0, width: "100%" }}>
              <SkeletonBox width="220px" height="30px" style={{ marginBottom: "12px" }} />
              <SkeletonBox width="160px" height="18px" style={{ marginBottom: "8px" }} />
              <SkeletonBox width="200px" height="16px" />
            </div>
          </div>
        </div>

        {/* Tabs skeleton */}
        <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
          {[1, 2, 3].map((i) => (
            <SkeletonBox key={i} width="120px" height="38px" style={{ borderRadius: "12px" }} />
          ))}
        </div>

        {/* Content skeleton */}
        <div
          style={{
            background: theme === "dark" ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.9)",
            border: theme === "dark" ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(0,0,0,0.05)",
            borderRadius: "16px",
            padding: screenSize.isMobile ? "16px" : "24px",
          }}
        >
          <SkeletonBox width="180px" height="24px" style={{ marginBottom: "20px" }} />
          <div style={{ display: "grid", gridTemplateColumns: screenSize.isMobile ? "1fr" : "repeat(2, 1fr)", gap: "16px" }}>
            {[1, 2, 3, 4].map((i) => (
              <SkeletonBox key={i} width="100%" height="60px" style={{ borderRadius: "12px" }} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ── Shared hover handlers ──────────────────────────────────────────────────
  const handleMouseEnter = (e) => {
    if (!screenSize.isMobile) Object.assign(e.currentTarget.style, getHoverStyles(theme));
  };
  const handleMouseLeave = (e) => {
    if (!screenSize.isMobile) Object.assign(e.currentTarget.style, getDefaultStyles(theme));
  };

  // ── Helper translators passed down as props ────────────────────────────────
  const getProjectStatus = (status) => ({
    Completed:   t("projectCompleted"),
    "In Progress": t("projectInProgress"),
    Planning:    t("projectPlanning"),
  }[status] ?? status);

  const getMetricLabel = (key) => ({
    productivity: t("productivity"),
    collaboration: t("collaboration"),
    innovation:   t("innovation"),
    leadership:   t("leadership"),
  }[key] ?? key);

  // ── Shared props passed to every tab ──────────────────────────────────────
  const sharedProps = { styles, theme, isRTL, screenSize, handleMouseEnter, handleMouseLeave, t };

  return (
    <div style={{...styles.container, marginTop: "-30px", marginBottom: "20px"}}>
      <div >
        
        
        <ProfileHeader
          {...sharedProps}
          language={language}
          profilePicture={profilePicture}
          isUploading={isUploading}
          employeeData={employeeData}
          fileInputRef={fileInputRef}
          triggerFileInput={triggerFileInput}
          handleFileSelect={handleFileSelect}
        />
        {/* Check-in Reminder Alert */}
        <CheckInReminderModal
          open={showReminderModal}
          onClose={() => setShowReminderModal(false)}
          hasTodayCheckIn={hasTodayCheckIn}
          hasCheckOut={hasTodayCheckOut}
          checkInTime={checkInTime}
          checkOutTime={checkOutTime}
          theme={theme}
          language={language}
          t={t}
        />


        {activeTab === "details" && (
          <DetailsTab
            {...sharedProps}
            employeeData={employeeData}
            recentActivities={recentActivities}
          />
        )}

        {activeTab === "performance" && (
          <PerformanceTab
            {...sharedProps}
            performanceData={performanceData}
            getMetricLabel={getMetricLabel}
          />
        )}

        {activeTab === "projects" && (
          <ProjectsTab
            {...sharedProps}
            employeeData={employeeData}
            getProjectStatus={getProjectStatus}
          />
        )}

      </div>

      <AlertModal
        open={alertModal.open}
        title={alertModal.title}
        message={alertModal.message}
        variant={alertModal.variant}
        onClose={closeAlert}
        theme={theme === "dark" ? "dark" : "light"}
        language={language}
      />
    </div>
  );
};

export default ProfilePage;